/**
 * Agent Performance — column model.
 *
 * One definition per column drives the header, the body cell and the column
 * picker, so the three can never drift apart. Deliberately the same shape as
 * the inbox's `ConversationTable.COLUMNS` (`stickyLeft` pins to the left edge,
 * `locked` means "not hideable") so the report inherits the inbox's behaviour
 * rather than reimplementing a second, subtly different table.
 *
 * Agent Name is the pinned identity column here, exactly as Conversation ID is
 * in the inbox: with sixteen metric columns scrolling horizontally, a row
 * whose subject has scrolled away is unreadable.
 */
import {
  User, Hash, Inbox, CheckCircle2, Loader, RotateCcw, ArrowDownLeft, ArrowUpRight,
  StickyNote, MessageSquare, Timer, TimerReset, ShieldCheck, ShieldAlert,
  Percent, PercentCircle, type LucideIcon,
} from 'lucide-react'

export interface ReportColumnDef {
  key: string
  label: string
  width: number
  stickyLeft?: number
  align?: 'left' | 'center' | 'right'
  icon?: LucideIcon
  /** Columns the user may not hide — without identity a row is unusable. */
  locked?: boolean
  /**
   * How the value is rendered by the generic `ReportDataTable`. Optional: the
   * two original reports render their own cells and ignore it.
   */
  type?: 'text' | 'mono' | 'number' | 'percent' | 'duration' | 'datetime' | 'badge' | 'boolean'
    | 'recording' | 'transcript'
}

/** Report id — the key this report's saved view is stored under on the user. */
export const AGENT_PERFORMANCE_REPORT_ID = 'conversation.agent-performance'

export const AGENT_PERFORMANCE_COLUMNS: ReportColumnDef[] = [
  { key: 'agentName', label: 'Agent Name', width: 220, stickyLeft: 0, locked: true, icon: User },
  { key: 'agentId', label: 'Agent ID', width: 200, icon: Hash },
  { key: 'assigned', label: 'Assigned', width: 200, align: 'right', icon: Inbox },
  { key: 'closed', label: 'Closed', width: 200, align: 'right', icon: CheckCircle2 },
  { key: 'workInProgress', label: 'Work in Progress', width: 200, align: 'right', icon: Loader },
  { key: 'reOpened', label: 'Re Opened', width: 200, align: 'right', icon: RotateCcw },
  { key: 'transferToAgent', label: 'Transfer to Agent', width: 200, align: 'right', icon: ArrowDownLeft },
  { key: 'transferByAgent', label: 'Transfer By Agent', width: 200, align: 'right', icon: ArrowUpRight },
  { key: 'notes', label: 'Notes', width: 200, align: 'right', icon: StickyNote },
  { key: 'responses', label: 'Responses', width: 200, align: 'right', icon: MessageSquare },
  { key: 'avgResponseSeconds', label: 'Average Response Time', width: 300, align: 'right', icon: Timer },
  { key: 'avgResolutionSeconds', label: 'Average Resolution Time', width: 300, align: 'right', icon: TimerReset },
  { key: 'withinResponseSla', label: 'Within Response SLA', width: 300, align: 'right', icon: ShieldCheck },
  { key: 'withinResolutionSla', label: 'Within Resolution SLA', width: 300, align: 'right', icon: ShieldAlert },
  { key: 'responseSlaPct', label: 'Response SLA%', width: 250, align: 'right', icon: Percent },
  { key: 'resolutionSlaPct', label: 'Resolution SLA%', width: 250, align: 'right', icon: PercentCircle },
]

/** Everything visible, in catalog order — what a user sees before saving a view. */
export const DEFAULT_VISIBLE_COLUMNS: string[] = AGENT_PERFORMANCE_COLUMNS.map((c) => c.key)

/**
 * Reconcile a stored view against the current catalog.
 *
 * A saved view is just a list of keys, so it goes stale the moment a column is
 * added or renamed. Unknown keys are dropped, locked columns are forced back
 * in, and a view that ends up empty falls back to the default set — a stale
 * preference can therefore never render an unusable table.
 *
 * Columns added AFTER a view was saved stay hidden on purpose: appearing
 * unannounced in a layout the user arranged is the more surprising behaviour.
 * Locked columns are the exception, since a row without identity is useless.
 */
export function sanitizeColumnOrder(saved: unknown): string[] {
  if (!Array.isArray(saved)) return DEFAULT_VISIBLE_COLUMNS
  const known = new Set(AGENT_PERFORMANCE_COLUMNS.map((c) => c.key))
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const key of saved) {
    if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  for (const c of AGENT_PERFORMANCE_COLUMNS) {
    if (c.locked && !seen.has(c.key)) {
      seen.add(c.key)
      ordered.unshift(c.key)
    }
  }
  return ordered.length > 0 ? ordered : DEFAULT_VISIBLE_COLUMNS
}

/** `9015` → `2h 30m 15s`. Null (nothing to average) reads as a dash. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—'
  const total = Math.max(0, Math.round(seconds))
  if (total === 0) return '0s'
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const parts: string[] = []
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  // Seconds are dropped once the value runs to hours — "2h 30m 15s" is
  // false precision for an average measured over a day.
  if (s > 0 && h === 0) parts.push(`${s}s`)
  return parts.join(' ') || '0s'
}

export function formatPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return `${value}%`
}

